import React, { useState } from 'react';
import { X, Play, CheckCircle, XCircle, Clock } from 'lucide-react';
import { workflowBuilderService } from '../../../services/workflowBuilderService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TestingSimulator = ({ workflow, nodes, onClose }) => {
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [mockData, setMockData] = useState({
    type: 'compliance_violation',
    severity: 'high',
    confidence: 0.85,
    title: 'Potential Organic Certification Violation',
    message: 'Pesticide application detected outside approved window'
  });

  const handleRunTest = async () => {
    try {
      setTesting(true);
      setTestResults(null);

      const results = await workflowBuilderService?.testWorkflow(workflow?.id, mockData);
      setTestResults(results);
    } catch (error) {
      setTestResults({
        success: false,
        executionLog: [{
          nodeId: 'error',
          nodeType: 'error',
          status: 'failed',
          message: error?.message,
          timestamp: new Date()?.toISOString()
        }]
      });
    } finally {
      setTesting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Test Workflow</h2>
            <p className="text-sm text-gray-600 mt-1">{workflow?.workflowName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Mock Data Configuration */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Mock Data Configuration</h3>
            <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
              <Select
                label="Alert Type"
                value={mockData?.type}
                onChange={(e) => setMockData(prev => ({ ...prev, type: e?.target?.value }))}
                options={[
                  { value: 'compliance_violation', label: 'Compliance Violation' },
                  { value: 'pest_outbreak', label: 'Pest Outbreak' },
                  { value: 'environmental_alert', label: 'Environmental Alert' }
                ]}
              />

              <Select
                label="Severity"
                value={mockData?.severity}
                onChange={(e) => setMockData(prev => ({ ...prev, severity: e?.target?.value }))}
                options={[
                  { value: 'low', label: 'Low' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'high', label: 'High' },
                  { value: 'critical', label: 'Critical' }
                ]}
              />

              <Input
                label="Confidence Score (0-1)"
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={mockData?.confidence}
                onChange={(e) => setMockData(prev => ({ ...prev, confidence: parseFloat(e?.target?.value) }))}
              />

              <Input
                label="Alert Title"
                value={mockData?.title}
                onChange={(e) => setMockData(prev => ({ ...prev, title: e?.target?.value }))}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alert Message
                </label>
                <textarea
                  value={mockData?.message}
                  onChange={(e) => setMockData(prev => ({ ...prev, message: e?.target?.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Workflow Summary */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Workflow Summary</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                This workflow contains <strong>{nodes?.length} node(s)</strong> that will be executed in sequence.
                The test will simulate the workflow execution without actually sending notifications or creating tasks.
              </p>
            </div>
          </div>

          {/* Test Results */}
          {testResults && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Test Results</h3>
              <div className="space-y-3">
                {testResults?.executionLog?.map((log, index) => {
                  const node = nodes?.find(n => n?.id === log?.nodeId);
                  return (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 ${
                        log?.status === 'success' ?'border-green-200 bg-green-50' :'border-red-200 bg-red-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {getStatusIcon(log?.status)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">
                              {node?.label || log?.nodeType}
                            </h4>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              log?.status === 'success' ?'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
                            }`}>
                              {log?.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{log?.message}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(log?.timestamp)?.toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={`mt-4 p-4 rounded-lg ${
                testResults?.success
                  ? 'bg-green-50 border border-green-200' :'bg-red-50 border border-red-200'
              }`}>
                <p className={`font-medium ${
                  testResults?.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {testResults?.success
                    ? '✓ Workflow test completed successfully'
                    : '✗ Workflow test failed'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <Button
            onClick={onClose}
            variant="outline"
          >
            Close
          </Button>
          <Button
            onClick={handleRunTest}
            disabled={testing}
            className="flex items-center gap-2"
          >
            <Play className="w-4 h-4" />
            {testing ? 'Running Test...' : 'Run Test'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TestingSimulator;